import "../../css/GlobalStyles.css";
import { Helmet } from "react-helmet";
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { LoginState, getAccountState } from "../../redux/slices/accountSlice";
import { MixpanelContext } from "../../context/AnalyticsService";
import { Colors } from "../../utils/colors";
import { useNavigate } from "../../hooks/useNavigate";
import RectangleButtonCreatorPanel from "../../components/Buttons/RectangleButtonCreatorPanel";
import HelpModal from "../../components/Containers/HelpModal";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { Icon } from "@iconify/react";
import UploadContacts from "../../components/CreatorDashboard/CreatorProfile/ImportContacts/UploadContacts";
import ConfirmDeleteModal from "../../components/Containers/ConfirmPopups/ConfirmDeleteModal";
import AssignImportedContacts from "../../components/CreatorDashboard/CreatorProfile/ImportContacts/AssignImportedContacts";
import {
  foundExistingSpreadsheetName,
  getTotalSpreadsheetFollowersUploaded,
  hasInProgressSpreadsheetImport,
} from "../../utils/spreadsheetUtils";
import {
  AudienceList,
  OnboardingFlowType,
  SpreadsheetInfo,
} from "@markit/common.types";
import ConfirmActionModal from "../../components/Containers/ConfirmPopups/ConfirmActionModal";
import SegmentContacts from "../../components/CreatorDashboard/CreatorProfile/ImportContacts/SegmentContacts";
import ConfirmImportContacts from "../../components/CreatorDashboard/CreatorProfile/ImportContacts/ConfirmImportContacts";
import { API } from "../../API";
import { hasSubscriptionUnlockedAdvancedData } from "@markit/common.utils";
import CreatorModeWrapper from "../../components/CreatorDashboard/CreatorModeWrapper";
import { NavigationId } from "../../navigation/AppParamList";
import MultiStageContainer from "../../components/Containers/MultiStageContainer";
import AlertContainer from "../../components/Containers/AlertContainer";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { getUserRef } from "../../utils/FirebaseUtils";
import { updateDoc } from "../../firebase";
import OnboardingTourStep from "../../components/Onboarding/OnboardingTourStep";
import { IMPORT_HISTORY_TAB_INDEX } from "../Profile/CreatorPanels/AudiencePanels/ImportPanel";
import { IMPORT_CONTACTS_ONBOARDING_STEPS } from "../../utils/onboardingFlowUtils";

export const FullNameColumnLabel = "Full Name";

export enum ImportContactsStage {
  UPLOAD = "Upload",
  ASSIGN = "Assign",
  SEGMENT = "Segment",
  CONFIRM = "Confirm",
}

const ImportContacts = () => {
  const { accountData, loggedIn, appInitialized, savedQuestions } =
    useSelector(getAccountState).account;
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [importStage, setImportStage] = useState(ImportContactsStage.UPLOAD);
  const [currNumImported, setCurrNumImported] = useState(0);
  // Upload Stage
  const [spreadsheet, setSpreadsheet] = useState<SpreadsheetInfo>();
  const [displayFile, setDisplayFile] = useState<{
    name: string;
    isError: boolean;
    message: string;
  }>({ name: "", isError: false, message: "" });
  const [permissionsChecked, setPermissionsChecked] = useState(false);
  // Assign Stage
  const [assignedColumns, setAssignedColumns] = useState<string[]>([]);
  const [fullNameAlert, setFullNameAlert] = useState<{
    header: string;
    subHeader: string;
  }>({ header: "", subHeader: "" });
  // Segment Stage
  const [selectedLists, setSelectedLists] = useState<AudienceList[]>([]);
  const [includeExistingContacts, setIncludeExistingContacts] = useState(false);

  const [importFailedModalVisible, setImportFailedModalVisible] =
    useState(false);
  const [importStartedAlertVisible, setImportStartedAlertVisible] =
    useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmImportVisible, setConfirmImportVisible] = useState(false);
  const [importInProgressAlertVisible, setImportInProgressAlertVisible] =
    useState(false);

  useEffect(() => {
    if (loggedIn === LoginState.LOGGED_IN && !appInitialized) {
      return;
    }
    // Handle Redirecting
    if (
      !hasSubscriptionUnlockedAdvancedData(accountData) &&
      !accountData.isAdmin
    ) {
      navigate(NavigationId.HOME_IMPORT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData, appInitialized]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (!appInitialized) {
      return;
    }
    // Check if there is an import in progress
    const hasInProgressImport = await hasInProgressSpreadsheetImport(
      accountData.uid
    );
    if (hasInProgressImport) {
      setImportInProgressAlertVisible(true);
    }
  }, [accountData, appInitialized]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (!appInitialized) {
      return;
    }
    // Get the number of imported contacts the user has uploaded already
    if (currNumImported === 0) {
      const numImportedContacts = await getTotalSpreadsheetFollowersUploaded(
        accountData.uid
      );
      setCurrNumImported(numImportedContacts);
    }
  }, [accountData, appInitialized, currNumImported]);

  // Reset fields if spreadsheet is changed
  useEffect(() => {
    if (importStage !== ImportContactsStage.ASSIGN) {
      if (assignedColumns) {
        setAssignedColumns([]);
      }
      if (selectedLists) {
        setSelectedLists([]);
      }
      if (includeExistingContacts) {
        setIncludeExistingContacts(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadsheet]);

  const numAvailableImports = useMemo(
    () => 500 - currNumImported,
    [currNumImported]
  );

  const numUnassignedColumns = useMemo(
    () => assignedColumns.filter((column) => column === "").length,
    [assignedColumns]
  );

  // The spreadsheet rows to show (whether to include existing contacts or not)
  const spreadsheetRows = useMemo(
    () =>
      spreadsheet
        ? includeExistingContacts
          ? spreadsheet.validRows.concat(spreadsheet.existingContactRows)
          : spreadsheet.validRows
        : [],
    [includeExistingContacts, spreadsheet]
  );

  const stageNames = useMemo(
    () => [
      ImportContactsStage.UPLOAD,
      ImportContactsStage.ASSIGN,
      ImportContactsStage.SEGMENT,
      ImportContactsStage.CONFIRM,
    ],
    []
  );

  const handleImportOnPress = useCallback(async () => {
    if (spreadsheet) {
      const foundFullNameColumnIndex = assignedColumns.findIndex(
        (column) => column === FullNameColumnLabel
      );
      // If no full name column is found (should never enter because we require it on earlier step)
      if (foundFullNameColumnIndex === -1) {
        setFullNameAlert({
          header: "Full Name is required to continue",
          subHeader:
            "We had trouble identifying the assigned Full Name column. Please go back and make sure the Full Name is assigned to a column.",
        });
        return;
      }
      const clearanceApproval =
        spreadsheet.validRows.length <= numAvailableImports ||
        accountData.importContactsApproval;
      const finalSpreadsheetInfo: SpreadsheetInfo = {
        ...spreadsheet,
        fileName: displayFile.name,
      };

      const assignedQuestions = savedQuestions.filter((question) =>
        assignedColumns.some((id) => question.id === id)
      );
      setImportLoading(true);
      await API.spreadsheet
        .uploadSpreadsheet({
          uid: accountData.uid,
          spreadsheet: finalSpreadsheetInfo,
          clearanceApproval: clearanceApproval,
          assignedColumns: assignedColumns,
          assignedQuestions: assignedQuestions,
          fullNameColumnIndex: foundFullNameColumnIndex,
          selectedListIds: selectedLists.map((list) => list.id),
          includeExistingContacts: includeExistingContacts,
        })
        .then(async (response) => {
          if (!response.success) {
            setImportLoading(false);
            setImportFailedModalVisible(true);
            return;
          }
          if (permissionsChecked) {
            const userRef = getUserRef(accountData.uid);
            updateDoc(userRef, { importContactsCheckedAgreement: true });
            mixpanel.track("Checked Agreement For Import Contacts", {
              distinct_id: accountData.uid,
              clearance_approval: clearanceApproval,
              valid_contacts: spreadsheet.validRows.length,
            });
          }
          setImportLoading(false);
          setImportStartedAlertVisible(true);
        })
        .catch((error) => {
          setImportLoading(false);
          setImportFailedModalVisible(true);
          console.error(error);
        });
    }
  }, [
    accountData.importContactsApproval,
    accountData.uid,
    assignedColumns,
    displayFile.name,
    includeExistingContacts,
    mixpanel,
    numAvailableImports,
    permissionsChecked,
    savedQuestions,
    selectedLists,
    spreadsheet,
  ]);

  // Add the checks before showing the confirmation import popup
  const continueOnPress = useCallback(async () => {
    const spreadsheetNameExists = await foundExistingSpreadsheetName(
      displayFile.name,
      accountData.uid
    );

    if (importStage === ImportContactsStage.UPLOAD) {
      if (spreadsheetNameExists || displayFile.name === "") {
        setDisplayFile({
          ...displayFile,
          isError: true,
          message:
            displayFile.name === ""
              ? "You must enter a display name."
              : "This display name has been used before. Choose another name.",
        });
        return;
      }
      setImportStage(ImportContactsStage.ASSIGN);
      mixpanel.track("Finished Import Contacts Stage", {
        distinct_id: accountData.uid,
        stage: ImportContactsStage.UPLOAD,
        spreadsheet_name: displayFile.name,
        valid_contacts: spreadsheet?.validRows.length,
      });
    } else if (importStage === ImportContactsStage.ASSIGN) {
      if (spreadsheetRows[0].fullName === "") {
        setFullNameAlert({
          header: "Full Name is required to continue",
          subHeader:
            "You must assign a column as Full Name in order to continue your upload.",
        });
        return;
      }
      if (numUnassignedColumns > 0) {
        setConfirmVisible(true);
        return;
      }
      setImportStage(ImportContactsStage.SEGMENT);
      mixpanel.track("Finished Import Contacts Stage", {
        distinct_id: accountData.uid,
        stage: ImportContactsStage.ASSIGN,
        assignedQuestionIds: assignedColumns,
      });
    } else if (importStage === ImportContactsStage.SEGMENT) {
      setImportStage(ImportContactsStage.CONFIRM);
      mixpanel.track("Finished Import Contacts Stage", {
        distinct_id: accountData.uid,
        stage: ImportContactsStage.SEGMENT,
        selectedListIds: selectedLists.map((list) => list.id),
      });
    } else if (importStage === ImportContactsStage.CONFIRM) {
      setConfirmImportVisible(true);
    }
  }, [
    accountData.uid,
    assignedColumns,
    displayFile,
    importStage,
    mixpanel,
    numUnassignedColumns,
    selectedLists,
    spreadsheet?.validRows.length,
    spreadsheetRows,
  ]);

  const navigateImportTab = useCallback(() => {
    navigate(NavigationId.HOME_IMPORT);
  }, [navigate]);

  const backOnPress = useCallback(() => {
    if (importStage === ImportContactsStage.UPLOAD) {
      if (spreadsheet) {
        setShowConfirmDelete(true);
      } else {
        navigateImportTab();
      }
    } else {
      setImportStage(
        importStage === ImportContactsStage.ASSIGN
          ? ImportContactsStage.UPLOAD
          : importStage === ImportContactsStage.SEGMENT
            ? ImportContactsStage.ASSIGN
            : ImportContactsStage.SEGMENT
      );
    }
  }, [importStage, navigateImportTab, spreadsheet]);

  return (
    <div>
      <Helmet>
        <title>{"Import Contacts"}</title>
        <meta name="og:title" content={"Import Contacts"} />
        <meta name="og:description" content={"Upload Spreadsheet"} />
      </Helmet>
      <CreatorModeWrapper>
        <div
          className="ColumnNormal"
          style={{ backgroundColor: Colors.WHITE1, paddingBottom: 100 }}
        >
          <div className="CreatorPanelContainer">
            <div className="AlignedRowSpaced">
              <h2>Import Contacts</h2>
              <RectangleButtonCreatorPanel
                title="Need Help?"
                onPress={() => setShowHelpModal(true)}
                iconName=""
              />
            </div>
          </div>
          <div className="CreatorPanelContainer">
            <MultiStageContainer
              currentStageIndex={stageNames.findIndex(
                (stage) => stage === importStage
              )}
              stageNames={stageNames}
              backOnPress={backOnPress}
              rightButton={
                <RectangleButton
                  buttonLabel={
                    <span style={{ fontSize: 14, fontWeight: 500 }}>
                      {importStage === ImportContactsStage.CONFIRM
                        ? "Confirm and Import"
                        : importStage === ImportContactsStage.SEGMENT
                          ? selectedLists.length > 0
                            ? "Add and Continue"
                            : "Skip to Confirm"
                          : "Continue"}
                    </span>
                  }
                  altColor={Colors.BLACK}
                  altTextColor={Colors.WHITE}
                  disabled={!spreadsheet || spreadsheetRows.length === 0}
                  altPaddingHorz={
                    importStage === ImportContactsStage.SEGMENT ? 20 : 40
                  }
                  altPaddingVert={14}
                  onPress={continueOnPress}
                  iconRight={
                    <Icon
                      icon="mdi:chevron-right"
                      height={16}
                      style={{
                        color:
                          !spreadsheet || spreadsheetRows.length === 0
                            ? Colors.GRAY1
                            : Colors.WHITE,
                      }}
                    />
                  }
                />
              }
            >
              {importStage === ImportContactsStage.UPLOAD ? (
                <>
                  {!spreadsheet ? (
                    <OnboardingTourStep
                      key="import-contacts-tour-step-1"
                      flowType={OnboardingFlowType.IMPORT_CONTACTS}
                      target=".onboarding-import-upload-target"
                      content={
                        <>
                          Import your CSV here to upload the contacts you own
                          straight onto Markit.
                          <br />
                          <br />
                          Make sure there is a column with valid phone numbers
                          and a column for the associated names. Click on
                          &quot;How to Format Spreadsheet&quot; to view more
                          details.
                        </>
                      }
                      stepNumber={1}
                      totalSteps={IMPORT_CONTACTS_ONBOARDING_STEPS}
                    />
                  ) : (
                    <OnboardingTourStep
                      key="import-contacts-tour-step-2"
                      flowType={OnboardingFlowType.IMPORT_CONTACTS}
                      target=".onboarding-import-uploaded-target"
                      content={
                        <>
                          You&apos;ve uploaded a valid spreadsheet!
                          <br />
                          <br />
                          Valid Contacts are ready to import, Failed Imports had
                          an issue like a missing or invalid phone number, and
                          Already Existing Contacts have already been uploaded
                          before.
                        </>
                      }
                      stepNumber={2}
                      totalSteps={IMPORT_CONTACTS_ONBOARDING_STEPS}
                      mountDelayMs={300}
                    />
                  )}
                  <UploadContacts
                    spreadsheet={spreadsheet}
                    setSpreadsheet={setSpreadsheet}
                    currNumImported={currNumImported}
                    displayFile={displayFile}
                    setDisplayFile={setDisplayFile}
                    permissionsChecked={permissionsChecked}
                    setPermissionsChecked={setPermissionsChecked}
                    includeExistingContacts={includeExistingContacts}
                    setIncludeExistingContacts={setIncludeExistingContacts}
                  />
                </>
              ) : spreadsheet && importStage === ImportContactsStage.ASSIGN ? (
                <>
                  <OnboardingTourStep
                    key="import-contacts-tour-step-3"
                    flowType={OnboardingFlowType.IMPORT_CONTACTS}
                    target=".onboarding-import-assign-target"
                    content={
                      <>
                        Review, assign, and combine columns here.
                        <br />
                        <br />
                        We&apos;ve auto-detected the phone number column. Now,
                        assign which column to use for the Full Name, and any
                        other columns you want to use for collected data.
                      </>
                    }
                    stepNumber={3}
                    totalSteps={IMPORT_CONTACTS_ONBOARDING_STEPS}
                  />
                  <AssignImportedContacts
                    spreadsheet={spreadsheet}
                    setSpreadsheet={setSpreadsheet}
                    includeExistingContacts={includeExistingContacts}
                    assignedColumns={assignedColumns}
                    setAssignedColumns={setAssignedColumns}
                  />
                </>
              ) : importStage === ImportContactsStage.SEGMENT ? (
                <>
                  <OnboardingTourStep
                    key="import-contacts-tour-step-4"
                    flowType={OnboardingFlowType.IMPORT_CONTACTS}
                    target=".onboarding-import-segment-target"
                    content="Optionally organize these contacts into a list here. This makes it easy to text only these contacts later."
                    stepNumber={4}
                    totalSteps={IMPORT_CONTACTS_ONBOARDING_STEPS}
                  />
                  <SegmentContacts
                    selectedLists={selectedLists}
                    setSelectedLists={setSelectedLists}
                  />
                </>
              ) : importStage === ImportContactsStage.CONFIRM ? (
                <>
                  <OnboardingTourStep
                    key="import-contacts-tour-step-5"
                    flowType={OnboardingFlowType.IMPORT_CONTACTS}
                    target=".onboarding-import-confirm-target"
                    content='Review the uploaded information, then hit "Confirm and Import" to finish.'
                    stepNumber={5}
                    totalSteps={IMPORT_CONTACTS_ONBOARDING_STEPS}
                  />
                  <ConfirmImportContacts
                    spreadsheet={spreadsheet}
                    setSpreadsheet={setSpreadsheet}
                    displayFile={displayFile}
                    selectedLists={selectedLists}
                    includeExistingContacts={includeExistingContacts}
                  />
                </>
              ) : null}
            </MultiStageContainer>
          </div>
        </div>
      </CreatorModeWrapper>
      <HelpModal showModal={showHelpModal} setShowModal={setShowHelpModal} />
      <ConfirmDeleteModal
        heading="Are you sure want to exit?"
        subtext="Your changes will be discarded."
        deleteButtonText="Exit"
        hideModal={!showConfirmDelete}
        setIsVisible={setShowConfirmDelete}
        deleteOnPress={navigateImportTab}
        icon={<Icon icon="ion:exit" height={40} />}
      />
      <ConfirmActionModal
        heading={"Ready to Import?"}
        subtext={`You are about to import ${spreadsheetRows.length} contacts from ${displayFile.name}. This cannot be undone.`}
        confirmButtonText={"Import"}
        icon={<Icon icon="ion:cloud-upload" height={40} />}
        hideModal={!confirmImportVisible}
        setIsVisible={setConfirmImportVisible}
        confirmOnPress={handleImportOnPress}
        loadingConfirm={importLoading}
      />
      <ConfirmActionModal
        heading={`You have ${numUnassignedColumns} unassigned columns`}
        subtext={
          "Are you sure you want to continue? Unassigned columns will not be included in this import."
        }
        confirmButtonText={"Continue"}
        icon={<Icon icon="ion:warning" height={40} />}
        hideModal={!confirmVisible}
        setIsVisible={setConfirmVisible}
        confirmOnPress={() => setImportStage(ImportContactsStage.SEGMENT)}
      />
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <Icon icon="ion:warning" height={40} />
            <span>{fullNameAlert.header}</span>
          </div>
        }
        subHeaderComp={fullNameAlert.subHeader}
        altButtonLabel="Got It"
        closeModal={() => setFullNameAlert({ header: "", subHeader: "" })}
        hideModal={!fullNameAlert.header}
      />
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <Icon icon="ion:warning" height={40} />
            <span>Import Already In Progress</span>
          </div>
        }
        subHeaderComp="You already have an import in progress. Please wait for it to finish before starting a new one."
        altButtonLabel="Got It"
        closeModal={() => {
          setImportInProgressAlertVisible(false);
          navigate(NavigationId.HOME_IMPORT);
        }}
        hideModal={!importInProgressAlertVisible}
      />
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <Icon icon="ion:warning" height={40} />
            <span>Import Failed</span>
          </div>
        }
        subHeaderComp="An error occurred while importing. Try again or contact our hotline for help."
        altButtonLabel="Got It"
        closeModal={() => setImportFailedModalVisible(false)}
        hideModal={!importFailedModalVisible}
      />
      <AlertContainer
        headerComp={
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <Icon icon="mdi:dots-circle" height={40} />
            <span>Import In Progress</span>
          </div>
        }
        subHeaderComp="Your import is now processing in the background. You can leave this page - you'll be able to view its status in the Import History panel. Depending on the size of the file, this may take a few minutes."
        altButtonLabel="Got It"
        closeModal={() => {
          setImportStartedAlertVisible(false);
          navigate(NavigationId.HOME_IMPORT, {
            state: { initialTab: IMPORT_HISTORY_TAB_INDEX },
          });
        }}
        hideModal={!importStartedAlertVisible}
      />
    </div>
  );
};

export default memo(ImportContacts);
